# Minimal variant for search/display - no tagTypeName, avoids payload bloat
fragment TagMinimalFragment on Tag {
  id
  type
  displayValue
  isActive
  attributes
  appliedCount
  tagAttributesMetaData {
    person {
      aggregateId
      preferredName
      firstName
      surname
      avatar
    }
  }
}

# Use TagFragmentWithAppliedCount instead
fragment TagFragment on Tag {
  id
  type
  displayValue
  isActive
  attributes
  appliedCount
  tagTypeName
  tagAttributesMetaData {
    person {
      aggregateId
      preferredName
      firstName
      surname
      avatar
    }
  }
}

fragment TagFragmentWithAppliedCount on Tag {
  id
  type
  displayValue
  isActive
  attributes
  appliedCount @include(if: $showAppliedCount)
  tagTypeName
  tagAttributesMetaData {
    person {
      aggregateId
      preferredName
      firstName
      surname
      avatar
    }
    people @include(if: $showTagOwners) {
      aggregateId
      preferredName
      firstName
      surname
      avatar
    }
  }
}

fragment TagPropertyFragment on TagProperty {
  id
  label
  value
  attributeType
  subtype
}

# Minimal fragment for allocation/caching - avoids tagAttributesMetaData payload
fragment EntityTagMinimalFragment on EntityTag {
  id
  tagId
  type
  replace
  entityId
  entityTagAttributes
  status
  tag {
    id
    displayValue
    type
    attributes
    isActive
  }
}

fragment EntityTagFragment on EntityTag {
  id
  tagId
  type
  replace
  entityId
  entityTagAttributes
  status
  tag {
    id
    displayValue
    type
    attributes
    appliedCount
    isActive
    tagAttributesMetaData {
      person {
        aggregateId
        preferredName
        firstName
        surname
        avatar
      }
    }
  }
}



fragment TagsEditorEntityTagFragment on EntityTag {
  id
  tagId
  type
  replace
  entityId
  entityTagAttributes
  status
  entityType
  tag {
    id
    displayValue
    type
    attributes
    appliedCount @include(if: $includeAppliedCount)
    isActive
    tagAttributesMetaData {
      person {
        aggregateId
        preferredName
        firstName
        surname
        avatar
      }
    }
  }
}



query getTagsForEntity(
  $entityId: ID!
  $tagTypes: [TagType]
  $tagStatus: String
  $entityType: String
  $includeAppliedCount: Boolean = false
) {
  tags: getTagsForEntityV2(
    entityId: $entityId
    tagTypes: $tagTypes
    tagStatus: $tagStatus
    entityType: $entityType
  ) {
    ...TagsEditorEntityTagFragment
  }
}

# Note: we don't use this query aside from a way of updating the cache for loadTagsAndConfigByType to pick up
query searchTags($tagTypes: [TagType], $tagIds: [ID]) {
  tags: searchTags(tagTypes: $tagTypes, tagIds: $tagIds) {
    ...TagMinimalFragment
  }
}
# TODO: Replace above with this one (does same thing, more usage-named)
query availableTags($tagTypes: [TagType], $limit: Int, $searchTerm: String) {
  tags: searchTags(
    tagTypes: $tagTypes
    count: $limit
    searchTerm: $searchTerm
  ) {
    ...TagMinimalFragment
  }
}

mutation addTagToEntity(
  $entityId: ID!
  $entityType: String
  $tagId: ID!
  $replace: Boolean!
  $comments: TaggingComments
  $includeAppliedCount: Boolean = false
) {
  result: addTagToEntity(
    entityId: $entityId
    entityType: $entityType
    tagId: $tagId
    replace: $replace
    comments: $comments
  ) {
    ...TagsEditorEntityTagFragment
  }
}

mutation updateTagsOnEntities($input: UpdateTagsOnEntitiesInput) {
  entityTagIds: updateTagsOnEntities(input: $input)
}

mutation addNewTag($input: AddNewTagInput!) {
  tag: addNewTag(input: $input) {
    ...TagMinimalFragment
  }
}

mutation addNewTagToEntity(
  $entityId: ID!
  $entityType: String
  $tagType: TagType!
  $displayValue: String!
  $attributes: JSON
  $replace: Boolean!
  $comments: TaggingComments
  $includeAppliedCount: Boolean = false
) {
  result: addNewTagToEntity(
    input: {
      entityId: $entityId
      entityType: $entityType
      type: $tagType
      displayValue: $displayValue
      attributes: $attributes
      replace: $replace
      comments: $comments
    }
  ) {
    ...TagsEditorEntityTagFragment
  }
}

mutation removeTagFromEntity(
  $entityId: ID!
  $entityType: String
  $tagId: ID!
  $comments: TaggingComments
) {
  result: removeTagFromEntity(
    entityId: $entityId
    entityType: $entityType
    tagId: $tagId
    comments: $comments
  ) {
    id
    tagId
    appliedCount
  }
}

query tagTypeConfigsForEntityType(
  $entityType: EntityType!
  $visibleIn: TagComponents
  $groupType: String
) {
  tagTypesConfig: getTagConfigList(
    input: {
      entityType: $entityType
      visibleIn: $visibleIn
      groupType: $groupType
    }
  ) {
    id
    name
    details
    allowMultipleOnEntity
    visibleIn
    isEditableByUsers
    isReadonly
    isGroupAssociation
    attributes
    showPreviousAppliedTags
    allowedToEditTagDisplayValue
    isPersonBadge
    isGroupBadge
    hideShowInfoIconInTagDisplay
    hideTagTypeInTagDisplay
  }
}

mutation updateTagValue($input: UpdateTagInput!) {
  tag: updateTag(input: $input) {
    ...TagMinimalFragment
  }
}

mutation updateEntityTag(
  $input: UpdateEntityTagInput!
  $includeAppliedCount: Boolean = true
) {
  tag: updateEntityTag(input: $input) {
    ...TagsEditorEntityTagFragment
  }
}
